import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { LucideIcon } from "lucide-react";
import {
  Users,
  MapPin,
  Building2,
  Crown,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Search,
  Shield,
  Star,
  Globe,
  Key,
  CheckCircle,
  XCircle,
  AlertTriangle,
  LogOut,
  Eye,
  Loader2,
  TrendingUp,
  Phone,
  DollarSign,
  Activity,
  Clock,
  Zap,
  AlertCircle,
  Power,
  BarChart3,
  RefreshCw,
  Settings2,
  User,
  ShieldAlert,
  CircleDollarSign,
  Bug,
  ExternalLink as ExternalLinkIcon,
  Trash2,
  Braces,
  Network,
  CheckCircle2,
  Code2,
  Gift,
  Copy,
  Plus,
} from "lucide-react";
import { useLocation } from "wouter";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

interface AdminLocation {
  id: string;
  name: string;
  city: string;
  state: string;
  category: string;
  gbpConnected: boolean;
  avgRating: number | null;
  totalReviews: number | null;
}

interface OAuthStatus {
  connected: boolean;
  googleEmail: string | null;
  expiresAt: string | null;
  hasRefreshToken: boolean;
}

interface AdminAccount {
  id: string;
  ownerId: string;
  email: string;
  fullName: string;
  role: string;
  tier: string;
  locationCapOverride: number | null;
  billingStatus: string;
  createdAt: string;
  isComped?: boolean;
  compTier?: string | null;
  compUntil?: string | null;
  compNote?: string | null;
  tosAcceptedAt?: string | null;
  tosVersion?: string | null;
  locationCount: number;
  oauth: OAuthStatus;
  locations: AdminLocation[];
}

interface AdminStats {
  totalUsers: number;
  totalLocations: number;
  connectedGbp: number;
  tiers: { free: number; starter: number; growth: number; dominator: number };
  mrr: number;
  paidClientCount: number;
}

const TIER_CONFIG: Record<string, { label: string; color: string; price: string; badgeClass: string }> = {
  free: { label: "Recon", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", price: "$1", badgeClass: "bg-zinc-500/10 text-zinc-400" },
  starter: { label: "Local Domination", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", price: "$399", badgeClass: "bg-orange-500/10 text-orange-400" },
  growth: { label: "Multi-Location Domination", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", price: "$599", badgeClass: "bg-blue-500/10 text-blue-400" },
  dominator: { label: "Market Monopoly", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", price: "$999", badgeClass: "bg-purple-500/10 text-purple-400" },
};

function StatCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: number | string; sub?: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
          <Icon className="w-4.5 h-4.5 text-indigo-400" />
        </div>
        <span className="text-[12px] text-white/40 uppercase font-semibold tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white font-['Outfit',sans-serif]">{value}</div>
      {sub && <div className="text-[11px] text-white/30 mt-0.5">{sub}</div>}
    </div>
  );
}

function OAuthBadge({ oauth }: { oauth: OAuthStatus }) {
  if (!oauth.connected) {
    return (
      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] gap-1" data-testid="badge-oauth-disconnected">
        <XCircle className="w-3 h-3" /> No OAuth
      </Badge>
    );
  }

  const expired = oauth.expiresAt ? new Date(oauth.expiresAt) < new Date() : false;
  const soonExpiring = oauth.expiresAt
    ? new Date(oauth.expiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000
    : false;

  if (expired && !oauth.hasRefreshToken) {
    return (
      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] gap-1" data-testid="badge-oauth-expired">
        <AlertTriangle className="w-3 h-3" /> Expired
      </Badge>
    );
  }

  return (
    <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] gap-1" data-testid="badge-oauth-connected">
      <Key className="w-3 h-3" /> {oauth.googleEmail || "Connected"}
      {soonExpiring && !oauth.hasRefreshToken && <AlertTriangle className="w-3 h-3 text-amber-400 ml-1" />}
    </Badge>
  );
}

interface GbpLocation {
  resourceName: string;
  title: string;
  address: string;
  city: string;
  state: string;
  category: string;
  phone: string;
  website: string;
  placeId: string;
  managed: boolean;
}

function GbpLocationsPanel({ userId, oauth }: { userId: string; oauth: OAuthStatus }) {
  const [showGbp, setShowGbp] = useState(false);

  const { data: gbpData, isLoading, refetch } = useQuery<{ connected: boolean; locations: GbpLocation[]; total: number; managed: number }>({
    queryKey: ["/api/admin/accounts", userId, "gbp-locations"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/accounts/${userId}/gbp-locations`, { credentials: "include" });
      return res.json();
    },
    enabled: showGbp,
  });

  if (!oauth.connected) {
    return (
      <div className="text-[12px] text-white/25 italic flex items-center gap-2 py-3">
        <XCircle className="w-3.5 h-3.5" /> No Google OAuth connected — cannot view GBP locations
      </div>
    );
  }

  if (!showGbp) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-[12px] h-8 mt-2"
        onClick={() => { setShowGbp(true); refetch(); }}
        data-testid={`button-view-gbp-${userId}`}
      >
        <Eye className="w-3.5 h-3.5 mr-1.5" />
        View All GBP Locations
      </Button>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-white/40 py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Fetching locations from Google...
      </div>
    );
  }

  const locs = gbpData?.locations || [];
  const unmanagedLocs = locs.filter(l => !l.managed);
  const managedLocs = locs.filter(l => l.managed);

  return (
    <div className="mt-3">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[11px] text-white/30 uppercase font-semibold tracking-wider">
          All GBP Locations ({locs.length})
        </div>
        {unmanagedLocs.length > 0 && (
          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/25 text-[10px] gap-1" data-testid="badge-upsell-count">
            <TrendingUp className="w-3 h-3" /> {unmanagedLocs.length} upsell opportunit{unmanagedLocs.length === 1 ? "y" : "ies"}
          </Badge>
        )}
      </div>
      {locs.length === 0 ? (
        <div className="text-[12px] text-white/25 italic">No locations found in GBP account</div>
      ) : (
        <div className="space-y-1.5">
          {[...unmanagedLocs, ...managedLocs].map((loc, i) => {
            const isManaged = loc.managed;
            const borderColor = isManaged ? "border-emerald-500/15" : "border-amber-500/15";
            const bgColor = isManaged ? "bg-white/[0.02]" : "bg-amber-500/[0.04]";
            const vStatus = loc.verificationStatus || "unknown";
            const vBadge = vStatus === "verified"
              ? { label: "Verified", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", icon: <CheckCircle className="w-2.5 h-2.5" /> }
              : vStatus === "suspended"
              ? { label: "Suspended", cls: "bg-red-500/15 text-red-400 border-red-500/25", icon: <XCircle className="w-2.5 h-2.5" /> }
              : vStatus === "pending"
              ? { label: "Pending", cls: "bg-amber-500/15 text-amber-400 border-amber-500/25", icon: <Loader2 className="w-2.5 h-2.5" /> }
              : vStatus === "unverified"
              ? { label: "Unverified", cls: "bg-orange-500/15 text-orange-400 border-orange-500/25", icon: <AlertTriangle className="w-2.5 h-2.5" /> }
              : { label: "Unknown", cls: "bg-white/5 text-white/30 border-white/10", icon: <Shield className="w-2.5 h-2.5" /> };
            return (
              <div key={loc.placeId || i} className={`px-3 py-2.5 rounded-lg ${bgColor} border ${borderColor}`} data-testid={`gbp-loc-${isManaged ? "managed" : "upsell"}-${i}`}>
                <div className="flex items-center gap-3">
                  {isManaged
                    ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    : <TrendingUp className="w-4 h-4 text-amber-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-white/80 font-medium truncate">{loc.title}</div>
                    <div className="text-[11px] text-white/35">{loc.category}{loc.city ? ` · ${loc.city}, ${loc.state}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge className={`${vBadge.cls} text-[9px] gap-0.5`}>
                      {vBadge.icon} {vBadge.label}
                    </Badge>
                    <Badge className={`${isManaged ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-amber-500/15 text-amber-400 border-amber-500/25"} text-[10px] shrink-0`}>
                      {isManaged ? "Managed" : "Not Managed"}
                    </Badge>
                  </div>
                </div>
                <div className="mt-1.5 ml-7 space-y-0.5">
                  {loc.fullAddress && (
                    <div className="text-[10px] text-white/30 flex items-center gap-1 truncate">
                      <MapPin className="w-2.5 h-2.5 shrink-0" /> {loc.fullAddress}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    {loc.phone && (
                      <div className="text-[10px] text-white/25 flex items-center gap-1">
                        <Phone className="w-2.5 h-2.5" />{loc.phone}
                      </div>
                    )}
                    {loc.website && (
                      <a href={loc.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400/60 hover:text-blue-400 flex items-center gap-1 truncate max-w-[250px]">
                        <Globe className="w-2.5 h-2.5 shrink-0" /> {loc.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                      </a>
                    )}
                    {loc.mapsUrl && (
                      <a href={loc.mapsUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-400/60 hover:text-emerald-400 flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5 shrink-0" /> View on Google Maps
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AccountRow({ account, onImpersonate, currentUserId }: { account: AdminAccount; onImpersonate: (userId: string) => void; currentUserId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [compOpen, setCompOpen] = useState(false);
  const [compTierDraft, setCompTierDraft] = useState<"starter" | "growth" | "dominator">(
    (account.compTier as any) || "dominator"
  );
  const [compExpires, setCompExpires] = useState<string>(
    account.compUntil ? new Date(account.compUntil).toISOString().slice(0, 10) : ""
  );
  const [compNote, setCompNote] = useState<string>(account.compNote || "");
  const { toast } = useToast();
  const tierConfig = TIER_CONFIG[account.tier] || TIER_CONFIG.free;
  const isSelf = account.ownerId === currentUserId;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/admin/accounts/${account.ownerId}`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      const bits = [
        `${data.deletedLocations} locations`,
        `${data.cancelledSubscriptions} Whop memberships cancelled`,
        ...(data.removedProtections ? [`${data.removedProtections} protections removed`] : []),
      ];
      toast({
        title: "Account deleted",
        description: `${account.email}: ${bits.join(" · ")}${data.errors?.length ? ` (${data.errors.length} warnings)` : ""}`,
      });
      setDeleteOpen(false);
      setDeleteConfirm("");
    },
    onError: (err: any) => toast({ title: "Failed to delete account", description: err?.message, variant: "destructive" }),
  });

  const tierMutation = useMutation({
    mutationFn: async (newTier: string) => {
      const res = await apiRequest("PATCH", `/api/admin/accounts/${account.id}/tier`, { tier: newTier });
      return res.json();
    },
    onSuccess: (_, newTier) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      toast({ title: "Tier updated", description: `${account.email} → ${newTier}` });
    },
    onError: () => toast({ title: "Failed to update tier", variant: "destructive" }),
  });

  const [capDraft, setCapDraft] = useState<string>(
    account.locationCapOverride != null ? String(account.locationCapOverride) : ""
  );
  const capMutation = useMutation({
    mutationFn: async (cap: number | null) => {
      const res = await apiRequest("PATCH", `/api/admin/accounts/${account.id}/location-cap`, { cap });
      return res.json();
    },
    onSuccess: (_, cap) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      toast({
        title: "Location cap updated",
        description: cap == null
          ? `${account.email}: override cleared (using ${account.tier} tier default)`
          : `${account.email}: capped at ${cap} location${cap === 1 ? "" : "s"}`,
      });
    },
    onError: (err: any) => toast({ title: "Failed to update cap", description: err?.message, variant: "destructive" }),
  });

  const commitCap = () => {
    const trimmed = capDraft.trim();
    if (trimmed === "") {
      if (account.locationCapOverride != null) capMutation.mutate(null);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n <= 0 || n > 10000) {
      toast({ title: "Invalid cap", description: "Enter a whole number between 1 and 10000, or leave blank to clear.", variant: "destructive" });
      setCapDraft(account.locationCapOverride != null ? String(account.locationCapOverride) : "");
      return;
    }
    if (n !== account.locationCapOverride) capMutation.mutate(n);
  };

  const grantCompMutation = useMutation({
    mutationFn: async () => {
      const expiresAt = compExpires ? new Date(`${compExpires}T23:59:59`).toISOString() : null;
      const res = await apiRequest("POST", `/api/admin/accounts/${account.id}/comp`, {
        tier: compTierDraft,
        expiresAt,
        note: compNote.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      toast({
        title: account.isComped ? "Comp updated" : "Comp granted",
        description: `${account.email} → ${compTierDraft}${compExpires ? ` until ${compExpires}` : " (no expiry)"}`,
      });
      setCompOpen(false);
    },
    onError: (err: any) => toast({ title: "Failed to grant comp", description: err?.message, variant: "destructive" }),
  });

  const revokeCompMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/admin/accounts/${account.id}/comp`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      toast({ title: "Comp revoked", description: `${account.email} → free` });
      setCompOpen(false);
    },
    onError: (err: any) => toast({ title: "Failed to revoke comp", description: err?.message, variant: "destructive" }),
  });

  const removeOAuthMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/admin/accounts/${account.ownerId}/oauth`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/accounts"] });
      toast({ title: "OAuth removed", description: `Google connection removed for ${account.email}` });
    },
    onError: () => toast({ title: "Failed to remove OAuth", variant: "destructive" }),
  });

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden" data-testid={`account-row-${account.id}`}>
      <div
        className="flex items-center gap-4 p-4 hover:bg-white/[0.02] cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/20 to-violet-500/10 flex items-center justify-center text-indigo-400 font-bold text-sm font-['Outfit',sans-serif]">
          {(account.fullName || account.email)[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium text-[14px] truncate">{account.fullName || account.email}</span>
            {account.role === "admin" && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">Admin</Badge>}
          </div>
          <div className="text-[12px] text-white/40">{account.email}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <OAuthBadge oauth={account.oauth} />
          {account.isComped && (
            <Badge
              className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] px-2"
              title={[
                `Comped at ${account.compTier || account.tier}`,
                account.compUntil ? `until ${new Date(account.compUntil).toLocaleDateString()}` : "no expiry",
                account.compNote ? `— ${account.compNote}` : "",
              ].filter(Boolean).join(" ")}
              data-testid={`badge-comped-${account.id}`}
            >
              <Gift className="w-3 h-3 mr-1" />COMPED
            </Badge>
          )}
          <Badge className={`${tierConfig.color} border text-[11px] px-2`} data-testid={`tier-badge-${account.id}`}>
            {tierConfig.label} {tierConfig.price} / 28d
          </Badge>
          <Badge className={`text-[10px] ${account.billingStatus === "active" ? "bg-green-500/10 text-green-400 border-green-500/20" : account.billingStatus === "trialing" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}>
            {account.billingStatus || "none"}
          </Badge>
          <div className="text-[12px] text-white/30 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {account.locationCount}
          </div>
          <Select
            value={account.tier}
            onValueChange={(v) => tierMutation.mutate(v)}
          >
            <SelectTrigger className="w-[130px] h-8 bg-white/5 border-white/10 text-white/70 text-[12px]" data-testid={`tier-select-${account.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="growth">Growth</SelectItem>
              <SelectItem value="dominator">Dominator</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1" title="Per-account location cap override. Leave blank to use the tier default.">
            <span className="text-[11px] text-white/40">Cap:</span>
            <Input
              type="number"
              min={1}
              placeholder="tier"
              value={capDraft}
              onChange={(e) => setCapDraft(e.target.value)}
              onBlur={commitCap}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              onClick={(e) => e.stopPropagation()}
              className="w-[70px] h-8 bg-white/5 border-white/10 text-white/70 text-[12px] px-2"
              data-testid={`input-location-cap-${account.id}`}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className={`text-[12px] h-8 px-2 ${account.isComped ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-white/15 text-white/70 hover:bg-white/5"}`}
            data-testid={`button-comp-${account.id}`}
            onClick={(e) => { e.stopPropagation(); setCompOpen(true); }}
            title={account.isComped ? "Edit comp grant" : "Grant complimentary tier"}
          >
            <Gift className="w-3.5 h-3.5 mr-1" />
            {account.isComped ? "Edit Comp" : "Comp"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 text-[12px] h-8"
            data-testid={`impersonate-${account.ownerId}`}
            onClick={(e) => { e.stopPropagation(); onImpersonate(account.ownerId); }}
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" />
            Enter Dashboard
          </Button>
          {!isSelf && (
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[12px] h-8 px-2"
              data-testid={`delete-account-${account.ownerId}`}
              onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
              title="Delete account"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
          {expanded ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />}
        </div>
      </div>

      <Dialog open={compOpen} onOpenChange={setCompOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`dialog-comp-${account.id}`} onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <Gift className="h-5 w-5" /> {account.isComped ? "Edit comp grant" : "Grant comp tier"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              Give <strong className="text-white">{account.email}</strong> a complimentary paid tier without
              charging them. Useful for testers, friends &amp; family, or trial extensions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-white/70">Tier</label>
              <Select value={compTierDraft} onValueChange={(v) => setCompTierDraft(v as any)}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid={`comp-tier-select-${account.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Local Domination ($399 / 28d)</SelectItem>
                  <SelectItem value="growth">Multi-Location Domination ($599 / 28d)</SelectItem>
                  <SelectItem value="dominator">Market Monopoly ($999 / 28d)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-white/70">
                Expires <span className="text-white/40">(optional — leave blank for no expiry)</span>
              </label>
              <Input
                type="date"
                value={compExpires}
                onChange={(e) => setCompExpires(e.target.value)}
                className="bg-white/5 border-white/10"
                data-testid={`comp-expires-${account.id}`}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-white/70">Note <span className="text-white/40">(optional)</span></label>
              <Input
                value={compNote}
                onChange={(e) => setCompNote(e.target.value)}
                placeholder="e.g. Beta tester, friends & family"
                maxLength={500}
                className="bg-white/5 border-white/10"
                data-testid={`comp-note-${account.id}`}
              />
            </div>

            {account.isComped && (
              <div className="text-[11px] text-white/40 pt-1">
                Currently comped at <span className="text-emerald-300">{account.compTier || account.tier}</span>
                {account.compUntil ? ` until ${new Date(account.compUntil).toLocaleDateString()}` : " (no expiry)"}
                {account.compNote ? ` — ${account.compNote}` : ""}.
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {account.isComped && (
              <Button
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 mr-auto"
                onClick={() => revokeCompMutation.mutate()}
                disabled={revokeCompMutation.isPending}
                data-testid={`button-comp-revoke-${account.id}`}
              >
                {revokeCompMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                Revoke
              </Button>
            )}
            <Button variant="ghost" onClick={() => setCompOpen(false)} data-testid={`button-comp-cancel-${account.id}`}>Cancel</Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-500/90 text-white"
              onClick={() => grantCompMutation.mutate()}
              disabled={grantCompMutation.isPending}
              data-testid={`button-comp-save-${account.id}`}
            >
              {grantCompMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              {account.isComped ? "Save" : "Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-md" data-testid={`dialog-delete-${account.ownerId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" /> Delete account permanently?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">This will permanently delete <strong className="text-white">{account.email}</strong> and:</span>
              <ul className="list-disc pl-5 text-[13px] space-y-1">
                <li>Cancel all active Whop memberships</li>
                <li>Remove listing protection monitoring</li>
                <li>Delete all {account.locationCount} location(s) and their data (reviews, posts, geogrid scans, citations…)</li>
                <li>Disconnect Google OAuth and remove all notifications, bug reports, and citation orders</li>
              </ul>
              <span className="block pt-2">This cannot be undone. Type <code className="bg-white/10 px-1.5 py-0.5 rounded text-orange-400">DELETE</code> below to confirm.</span>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="bg-white/5 border-white/10"
            data-testid="input-delete-confirm"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} data-testid="button-delete-cancel">Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== "DELETE" || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              data-testid="button-delete-confirm"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {expanded && (
        <div className="border-t border-white/[0.06] bg-white/[0.01] px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] text-white/30 uppercase font-semibold tracking-wider mb-2 px-2">Locations ({account.locations.length})</div>
              {account.locations.length > 0 ? (
                <div className="space-y-2">
                  {account.locations.map(loc => (
                    <div key={loc.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]" data-testid={`location-${loc.id}`}>
                      <Building2 className="w-4 h-4 text-white/30" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-white/80 font-medium truncate">{loc.name}</div>
                        <div className="text-[11px] text-white/35">{loc.category} · {loc.city}, {loc.state}</div>
                      </div>
                      {loc.gbpConnected && (
                        <Badge className="bg-green-500/15 text-green-400 border-green-500/20 text-[10px]">
                          <Globe className="w-3 h-3 mr-1" /> GBP
                        </Badge>
                      )}
                      {loc.avgRating !== null && loc.avgRating > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-amber-400">
                          <Star className="w-3 h-3 fill-amber-400" /> {loc.avgRating}
                          <span className="text-white/25">({loc.totalReviews})</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[13px] text-white/20 italic px-2">No locations set up</div>
              )}
            </div>

            <div>
              <div className="text-[11px] text-white/30 uppercase font-semibold tracking-wider mb-2 px-2">Account Details</div>
              <div className="space-y-1.5 px-2">
                <div className="flex justify-between items-center text-[12px]">
                  <span className="text-white/40">Google OAuth</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/70">{account.oauth.connected ? (account.oauth.googleEmail || "Connected") : "Not connected"}</span>
                    {account.oauth.connected && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-6 px-2"
                        data-testid={`button-remove-oauth-${account.ownerId}`}
                        onClick={() => {
                          if (confirm(`Remove Google OAuth for ${account.email}? They will need to reconnect.`)) {
                            removeOAuthMutation.mutate();
                          }
                        }}
                        disabled={removeOAuthMutation.isPending}
                      >
                        {removeOAuthMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-white/40">Refresh Token</span>
                  <span className={account.oauth.hasRefreshToken ? "text-green-400" : "text-red-400"}>{account.oauth.hasRefreshToken ? "Yes" : "No"}</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-white/40">Token Expires</span>
                  <span className="text-white/70">{account.oauth.expiresAt ? new Date(account.oauth.expiresAt).toLocaleString() : "N/A"}</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-white/40">Created</span>
                  <span className="text-white/70">{account.createdAt ? new Date(account.createdAt).toLocaleDateString() : "N/A"}</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-white/40">Billing</span>
                  <span className="text-white/70">{account.billingStatus || "none"}</span>
                </div>
              </div>

              <div className="mt-4 px-2">
                <div className="text-[11px] text-white/30 uppercase font-semibold tracking-wider mb-2">GBP Account Locations</div>
                <GbpLocationsPanel userId={account.ownerId} oauth={account.oauth} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApiUsageTab() {
  const { toast } = useToast();
  const [hours, setHours] = useState(24);
  const [subView, setSubView] = useState<"overview" | "clients" | "ai-calls" | "logs">("overview");
  const [budgetInput, setBudgetInput] = useState("");

  const safeFetch = async (url: string) => {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error(`API error ${r.status}`);
    return r.json();
  };
  const { data: statsData, isLoading: statsLoading, isError: statsError } = useQuery<any>({
    queryKey: ["/api/admin/api-logs/stats", hours],
    queryFn: () => safeFetch(`/api/admin/api-logs/stats?hours=${hours}`),
    refetchInterval: 15000,
  });
  const { data: logs, isLoading: logsLoading, isError: logsError } = useQuery<any[]>({
    queryKey: ["/api/admin/api-logs", hours],
    queryFn: () => safeFetch(`/api/admin/api-logs?hours=${hours}`),
    refetchInterval: subView === "logs" ? 30000 : false,
    enabled: subView === "logs",
  });
  const { data: clientUsage, isError: clientError } = useQuery<any[]>({
    queryKey: ["/api/admin/api-logs/by-client", hours],
    queryFn: () => safeFetch(`/api/admin/api-logs/by-client?hours=${hours}`),
    refetchInterval: subView === "clients" ? 30000 : false,
    enabled: subView === "clients",
  });
  const { data: hourlyData } = useQuery<any[]>({
    queryKey: ["/api/admin/api-logs/hourly", hours],
    queryFn: () => safeFetch(`/api/admin/api-logs/hourly?hours=${hours}`),
    refetchInterval: subView === "overview" ? 30000 : false,
    enabled: subView === "overview",
  });
  const { data: logSummary } = useQuery<any>({
    queryKey: ["/api/admin/api-logs/summary"],
    queryFn: () => safeFetch("/api/admin/api-logs/summary"),
    enabled: subView === "overview",
  });

  const controlMutation = useMutation({
    mutationFn: async (body: { action: string; value?: any }) => {
      const res = await apiRequest("POST", "/api/admin/ai-controls", body);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-logs/stats"] });
      toast({ title: "Controls updated", description: `Budget status: ${data.status?.budget_status || "OK"}` });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const aiBudget = statsData?.aiBudget;
  const maxRpm = statsData?.rpm?.length ? Math.max(...statsData.rpm.map((r: any) => r.count), 60) : 60;
  const maxHourly = hourlyData?.length ? Math.max(...hourlyData.map((h: any) => h.total), 1) : 1;

  const subTabs = [
    { id: "overview" as const, label: "Overview", icon: BarChart3 },
    { id: "clients" as const, label: "By Client", icon: User },
    { id: "ai-calls" as const, label: "AI Calls", icon: Zap },
    { id: "logs" as const, label: "Raw Logs", icon: Activity },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white/90">API Monitoring Dashboard</h2>
        <div className="flex items-center gap-3">
          <Select value={String(hours)} onValueChange={v => setHours(Number(v))}>
            <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white/80 text-[13px]" data-testid="select-hours">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1 hour</SelectItem>
              <SelectItem value="6">Last 6 hours</SelectItem>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {statsError && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-red-400 text-[13px]">Failed to load API stats. You may need to log in as admin.</div>
      )}
      {statsLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 rounded-xl bg-white/[0.03] animate-pulse" />)}
        </div>
      ) : statsData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="text-[12px] text-white/40 uppercase tracking-wider">Total Requests</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="text-total-requests">{statsData.totalRequests.toLocaleString()}</div>
            <div className="text-[11px] text-white/30 mt-1">Last {hours}h</div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-[12px] text-white/40 uppercase tracking-wider">Errors</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="text-total-errors">{statsData.totalErrors}</div>
            <div className="text-[11px] text-white/30 mt-1">{statsData.totalRequests > 0 ? ((statsData.totalErrors / statsData.totalRequests) * 100).toFixed(1) : 0}% error rate</div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-[12px] text-white/40 uppercase tracking-wider">Peak RPM</span>
            </div>
            <div className={`text-2xl font-bold ${statsData.peakRpm > 50 ? "text-red-400" : statsData.peakRpm > 30 ? "text-yellow-400" : "text-white"}`} data-testid="text-peak-rpm">{statsData.peakRpm}</div>
            <div className="text-[11px] text-white/30 mt-1">Limit: 60/min</div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center gap-2 mb-2">
              <CircleDollarSign className="w-4 h-4 text-[#00D4AA]" />
              <span className="text-[12px] text-white/40 uppercase tracking-wider">AI Spend</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="text-ai-spend">${((aiBudget?.today_spend_cents || 0) / 100).toFixed(2)}</div>
            <div className="text-[11px] text-white/30 mt-1">of ${((aiBudget?.daily_budget_cents || 500) / 100).toFixed(2)} budget</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-white/[0.06] pb-0">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubView(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-t-lg transition-colors ${
              subView === tab.id
                ? "text-white bg-white/[0.05] border-b-2 border-[#00D4AA]"
                : "text-white/40 hover:text-white/60"
            }`}
            data-testid={`tab-api-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {subView === "overview" && statsData && (
        <div className="space-y-6">
          {aiBudget && (
            <div className={`rounded-xl border p-5 ${
              aiBudget.budget_status === "BLOCKED" ? "bg-red-500/10 border-red-500/30" :
              aiBudget.budget_status === "WARNING" ? "bg-yellow-500/10 border-yellow-500/30" :
              "bg-white/[0.03] border-white/[0.06]"
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-white/50" />
                  <h3 className="text-[14px] text-white/70 font-semibold">AI Spend Controls</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    aiBudget.budget_status === "BLOCKED" ? "bg-red-500/20 text-red-400" :
                    aiBudget.budget_status === "WARNING" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-green-500/20 text-green-400"
                  }`} data-testid="text-budget-status">{aiBudget.budget_status}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Today's Spend</div>
                  <div className="text-xl font-bold text-white" data-testid="text-today-spend">${(aiBudget.today_spend_cents / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Daily Budget</div>
                  <div className="text-xl font-bold text-white/60">${(aiBudget.daily_budget_cents / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">AI Calls Today</div>
                  <div className="text-xl font-bold text-white">{aiBudget.calls_today}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Input Tokens</div>
                  <div className="text-xl font-bold text-white/60">{(aiBudget.total_input_tokens / 1000).toFixed(1)}k</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Output Tokens</div>
                  <div className="text-xl font-bold text-white/60">{(aiBudget.total_output_tokens / 1000).toFixed(1)}k</div>
                </div>
              </div>

              <div className="h-2.5 rounded-full bg-white/5 overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full transition-all ${
                    aiBudget.budget_status === "BLOCKED" ? "bg-red-500" :
                    aiBudget.budget_status === "WARNING" ? "bg-yellow-500" :
                    "bg-[#00D4AA]"
                  }`}
                  style={{ width: `${Math.min((aiBudget.today_spend_cents / (aiBudget.daily_budget_cents || 1)) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-white/20 mb-4">
                <span>$0</span>
                <span>Alert: ${(aiBudget.alert_threshold_cents / 100).toFixed(2)}</span>
                <span>Limit: ${(aiBudget.daily_budget_cents / 100).toFixed(2)}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/[0.06]">
                <Button
                  size="sm"
                  variant={aiBudget.kill_switch ? "destructive" : "outline"}
                  className={`text-[12px] h-8 gap-1.5 ${!aiBudget.kill_switch ? "border-white/10 text-white/60 hover:text-white" : ""}`}
                  onClick={() => controlMutation.mutate({ action: "toggle_kill_switch", value: !aiBudget.kill_switch })}
                  disabled={controlMutation.isPending}
                  data-testid="button-kill-switch"
                >
                  <Power className="w-3.5 h-3.5" />
                  {aiBudget.kill_switch ? "Kill Switch ON" : "Kill Switch OFF"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-[12px] h-8 gap-1.5 border-white/10 text-white/60 hover:text-white"
                  onClick={() => controlMutation.mutate({ action: "reset_budget" })}
                  disabled={controlMutation.isPending}
                  data-testid="button-reset-budget"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Reset Today
                </Button>
                <div className="flex items-center gap-1.5 ml-auto">
                  <input
                    type="number"
                    placeholder="New budget ($)"
                    value={budgetInput}
                    onChange={e => setBudgetInput(e.target.value)}
                    className="w-[110px] h-8 px-2 rounded-md bg-white/5 border border-white/10 text-white/80 text-[12px] placeholder:text-white/20"
                    data-testid="input-budget-amount"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[12px] h-8 gap-1 border-white/10 text-white/60 hover:text-white"
                    onClick={() => {
                      const cents = Math.round(parseFloat(budgetInput) * 100);
                      if (cents > 0) {
                        controlMutation.mutate({ action: "set_budget", value: cents });
                        setBudgetInput("");
                      }
                    }}
                    disabled={controlMutation.isPending || !budgetInput}
                    data-testid="button-set-budget"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Set
                  </Button>
                </div>
              </div>
            </div>
          )}

          {hourlyData && hourlyData.length > 0 && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
              <h3 className="text-[13px] text-white/50 mb-3 font-medium">Hourly API Volume</h3>
              <div className="flex items-end gap-[2px] h-36">
                {hourlyData.map((h: any, i: number) => {
                  const gbpPct = h.total > 0 ? (h.gbp / h.total) * 100 : 0;
                  const aiPct = h.total > 0 ? (h.ai / h.total) * 100 : 0;
                  const serpPct = h.total > 0 ? (h.serp / h.total) * 100 : 0;
                  const otherPct = 100 - gbpPct - aiPct - serpPct;
                  const barH = Math.max((h.total / maxHourly) * 100, 3);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ height: "100%" }}>
                      <div className="w-full rounded-t-sm overflow-hidden" style={{ height: `${barH}%` }}>
                        {gbpPct > 0 && <div className="w-full bg-blue-500/60" style={{ height: `${gbpPct}%` }} />}
                        {aiPct > 0 && <div className="w-full bg-purple-500/60" style={{ height: `${aiPct}%` }} />}
                        {serpPct > 0 && <div className="w-full bg-orange-500/60" style={{ height: `${serpPct}%` }} />}
                        {otherPct > 0 && <div className="w-full bg-white/10" style={{ height: `${otherPct}%` }} />}
                      </div>
                      <div className="absolute bottom-full mb-1 bg-black/90 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                        <div>{new Date(h.hour).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                        <div>Total: {h.total} | Err: {h.errors}</div>
                        <div className="text-blue-400">GBP: {h.gbp}</div>
                        <div className="text-purple-400">AI: {h.ai}</div>
                        <div className="text-orange-400">SERP: {h.serp}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/20">
                <span>{hourlyData.length > 0 ? new Date(hourlyData[0].hour).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500/60" />GBP</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-500/60" />AI</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-500/60" />SERP</span>
                </div>
                <span>now</span>
              </div>
            </div>
          )}

          {statsData.rpm?.length > 0 && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
              <h3 className="text-[13px] text-white/50 mb-3 font-medium">Requests Per Minute (Rate Limit Monitor)</h3>
              <div className="flex items-end gap-px h-28">
                {statsData.rpm.slice(-60).map((r: any, i: number) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                    <div
                      className={`w-full rounded-t-sm transition-colors ${r.count > 50 ? "bg-red-500/80" : r.count > 30 ? "bg-yellow-500/60" : "bg-blue-500/50"}`}
                      style={{ height: `${Math.max((r.count / maxRpm) * 100, 2)}%` }}
                    />
                    <div className="absolute bottom-full mb-1 bg-black/90 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                      {r.count} req/min
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-white/20">
                <span>oldest</span>
                <span className="text-red-400/50">— 60/min limit —</span>
                <span>newest</span>
              </div>
            </div>
          )}

          {statsData.stats?.length > 0 && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06]">
                <h3 className="text-[13px] text-white/50 font-medium">Breakdown by Service & Endpoint</h3>
              </div>
              <div className="max-h-[300px] overflow-auto">
                <table className="w-full min-w-[480px] text-[13px]">
                  <thead className="sticky top-0 bg-[#0a0e17]">
                    <tr className="border-b border-white/[0.04]">
                      <th className="text-left px-4 py-2 text-white/30 font-medium">Service</th>
                      <th className="text-left px-4 py-2 text-white/30 font-medium">Endpoint</th>
                      <th className="text-right px-4 py-2 text-white/30 font-medium">Requests</th>
                      <th className="text-right px-4 py-2 text-white/30 font-medium">Errors</th>
                      <th className="text-right px-4 py-2 text-white/30 font-medium">Avg Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsData.stats.map((s: any, i: number) => (
                      <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                        <td className="px-4 py-2 text-white/70">{s.service}</td>
                        <td className="px-4 py-2 text-white/50 font-mono text-[12px]">{s.endpoint}</td>
                        <td className="px-4 py-2 text-right text-white/70">{s.count}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={s.errorCount > 0 ? "text-red-400" : "text-white/30"}>{s.errorCount}</span>
                        </td>
                        <td className="px-4 py-2 text-right text-white/50">{s.avgDuration}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {logSummary && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
              <h3 className="text-[13px] text-white/50 mb-3 font-medium">Log Storage & Retention</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Total Stored</div>
                  <div className="text-lg font-bold text-white" data-testid="text-total-logs">{logSummary.totalLogs.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Today</div>
                  <div className="text-lg font-bold text-white">{logSummary.todayCount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Yesterday</div>
                  <div className="text-lg font-bold text-white/60">{logSummary.yesterdayCount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Retention</div>
                  <div className="text-lg font-bold text-white/60">{logSummary.retentionDays} days</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30 mb-0.5">Oldest Log</div>
                  <div className="text-[13px] font-medium text-white/50">{logSummary.oldestLog ? new Date(logSummary.oldestLog).toLocaleDateString() : "—"}</div>
                </div>
              </div>
              {logSummary.byService?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <div className="text-[11px] text-white/30 mb-2">By Service (all time / today)</div>
                  <div className="flex flex-wrap gap-2">
                    {logSummary.byService.map((s: any) => (
                      <span key={s.service} className="px-2 py-1 rounded bg-white/5 text-[11px] text-white/50">
                        {s.service}: {s.total.toLocaleString()} <span className="text-white/25">({s.today} today)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {subView === "clients" && (
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <h3 className="text-[13px] text-white/50 font-medium">API Usage by Client (Last {hours}h)</h3>
          </div>
          {clientError ? (
            <div className="p-4 text-red-400 text-[13px]">Failed to load client usage data</div>
          ) : !clientUsage?.length ? (
            <div className="p-6 text-center text-white/20 text-[13px]">No client usage data</div>
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full min-w-[520px] text-[13px]">
                <thead className="sticky top-0 bg-[#0a0e17]">
                  <tr className="border-b border-white/[0.04]">
                    <th className="text-left px-4 py-2 text-white/30 font-medium">Client</th>
                    <th className="text-right px-4 py-2 text-white/30 font-medium">Total Calls</th>
                    <th className="text-right px-4 py-2 text-white/30 font-medium">Errors</th>
                    <th className="text-right px-4 py-2 text-white/30 font-medium">Avg Duration</th>
                    <th className="text-left px-4 py-2 text-white/30 font-medium">Service Split</th>
                  </tr>
                </thead>
                <tbody>
                  {clientUsage.map((c: any, i: number) => {
                    const totalAll = clientUsage.reduce((s: number, x: any) => s + x.totalCalls, 0);
                    const pct = totalAll > 0 ? ((c.totalCalls / totalAll) * 100).toFixed(1) : "0";
                    return (
                      <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`row-client-usage-${i}`}>
                        <td className="px-4 py-2.5">
                          <div className="text-white/80 text-[13px]">{c.name}</div>
                          <div className="text-white/30 text-[11px]">{c.email}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="text-white/80 font-medium">{c.totalCalls.toLocaleString()}</div>
                          <div className="text-white/25 text-[11px]">{pct}%</div>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={c.totalErrors > 0 ? "text-red-400 font-medium" : "text-white/30"}>{c.totalErrors}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-white/50">{c.avgDuration}ms</td>
                        <td className="px-4 py-2.5">
                          {c.services && typeof c.services === "object" && (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(c.services).sort((a: any, b: any) => b[1] - a[1]).slice(0, 3).map(([svc, cnt]: [string, any]) => (
                                <span key={svc} className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-white/40">
                                  {svc}: {cnt}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subView === "ai-calls" && (
        <div className="space-y-4">
          {aiBudget && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] text-white/50 font-medium">AI Budget Summary</h3>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white/30">Model: {aiBudget.model}</span>
                  <span className="text-[11px] text-white/30">Gateway: {aiBudget.gateway}</span>
                  <span className="text-[11px] text-white/30">Max tokens: {aiBudget.max_tokens_per_request}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                <div>
                  <div className="text-[11px] text-white/30">Calls Today</div>
                  <div className="text-lg font-bold text-white">{aiBudget.calls_today}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30">Spend</div>
                  <div className="text-lg font-bold text-white">${(aiBudget.today_spend_cents / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30">Budget</div>
                  <div className="text-lg font-bold text-white/60">${(aiBudget.daily_budget_cents / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30">Input Tokens</div>
                  <div className="text-lg font-bold text-white/60">{(aiBudget.total_input_tokens / 1000).toFixed(1)}k</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30">Output Tokens</div>
                  <div className="text-lg font-bold text-white/60">{(aiBudget.total_output_tokens / 1000).toFixed(1)}k</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/30">Avg Cost/Call</div>
                  <div className="text-lg font-bold text-white/60">
                    ${aiBudget.calls_today > 0 ? (aiBudget.today_spend_cents / aiBudget.calls_today / 100).toFixed(3) : "0.00"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
            <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="text-[13px] text-white/50 font-medium">Recent AI Calls (In-Memory)</h3>
              <span className="text-[11px] text-white/25">{aiBudget?.recent_calls?.length || 0} entries</span>
            </div>
            {!aiBudget?.recent_calls?.length ? (
              <div className="p-6 text-center text-white/20 text-[13px]">No AI calls recorded today</div>
            ) : (
              <div className="max-h-[400px] overflow-auto">
                <table className="w-full min-w-[640px] text-[12px]">
                  <thead className="sticky top-0 bg-[#0a0e17]">
                    <tr className="border-b border-white/[0.04]">
                      <th className="text-left px-3 py-2 text-white/25 font-medium">Time</th>
                      <th className="text-left px-3 py-2 text-white/25 font-medium">Endpoint</th>
                      <th className="text-left px-3 py-2 text-white/25 font-medium">Model</th>
                      <th className="text-right px-3 py-2 text-white/25 font-medium">Input</th>
                      <th className="text-right px-3 py-2 text-white/25 font-medium">Output</th>
                      <th className="text-right px-3 py-2 text-white/25 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...aiBudget.recent_calls].reverse().map((call: any, i: number) => (
                      <tr key={i} className="border-b border-white/[0.02] hover:bg-white/[0.02]" data-testid={`row-ai-call-${i}`}>
                        <td className="px-3 py-1.5 text-white/30 whitespace-nowrap">
                          {new Date(call.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-1.5 text-white/50 font-mono text-[11px] max-w-[200px] truncate">{call.endpoint}</td>
                        <td className="px-3 py-1.5 text-purple-400/70 text-[11px]">{call.model?.split("-").slice(0, 3).join("-") || "—"}</td>
                        <td className="px-3 py-1.5 text-right text-white/40">{(call.input_tokens / 1000).toFixed(1)}k</td>
                        <td className="px-3 py-1.5 text-right text-white/40">{(call.output_tokens / 1000).toFixed(1)}k</td>
                        <td className="px-3 py-1.5 text-right">
                          <span className={`font-medium ${call.cost_cents > 5 ? "text-yellow-400" : call.cost_cents > 1 ? "text-white/60" : "text-white/30"}`}>
                            ${(call.cost_cents / 100).toFixed(4)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {subView === "logs" && (
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <h3 className="text-[13px] text-white/50 font-medium">Recent API Calls (All Services)</h3>
            <span className="text-[11px] text-white/25">{logs?.length || 0} entries</span>
          </div>
          {logsLoading ? (
            <div className="p-6 text-center text-white/20 text-[13px]">Loading logs...</div>
          ) : !logs?.length ? (
            <div className="p-6 text-center text-white/20 text-[13px]">No API calls logged yet</div>
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full min-w-[720px] text-[12px]">
                <thead className="sticky top-0 bg-[#0a0e17]">
                  <tr className="border-b border-white/[0.04]">
                    <th className="text-left px-3 py-2 text-white/25 font-medium">Time</th>
                    <th className="text-left px-3 py-2 text-white/25 font-medium">Service</th>
                    <th className="text-left px-3 py-2 text-white/25 font-medium">Endpoint</th>
                    <th className="text-center px-3 py-2 text-white/25 font-medium">Method</th>
                    <th className="text-center px-3 py-2 text-white/25 font-medium">Status</th>
                    <th className="text-right px-3 py-2 text-white/25 font-medium">Duration</th>
                    <th className="text-left px-3 py-2 text-white/25 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => (
                    <tr key={log.id} className="border-b border-white/[0.02] hover:bg-white/[0.02]">
                      <td className="px-3 py-1.5 text-white/30 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-3 py-1.5 text-white/50">{log.service}</td>
                      <td className="px-3 py-1.5 text-white/40 font-mono text-[11px] max-w-[180px] truncate">{log.endpoint}</td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${log.method === "GET" ? "bg-green-500/15 text-green-400" : log.method === "POST" ? "bg-blue-500/15 text-blue-400" : log.method === "PUT" ? "bg-yellow-500/15 text-yellow-400" : "bg-red-500/15 text-red-400"}`}>
                          {log.method}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`text-[11px] font-medium ${log.statusCode >= 400 ? "text-red-400" : log.statusCode >= 300 ? "text-yellow-400" : "text-green-400"}`}>
                          {log.statusCode}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-white/40">{log.durationMs}ms</td>
                      <td className="px-3 py-1.5 text-red-400/60 text-[11px] max-w-[150px] truncate">{log.error || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");

  const initialParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialTabParam = initialParams.get("tab");
  const initialOrderParam = initialParams.get("order") || undefined;
  const validTabs = ["accounts", "api-usage", "bug-reports", "schema-installs", "sweeper", "ff-links"] as const;
  const initialTab = (validTabs as readonly string[]).includes(initialTabParam || "")
    ? (initialTabParam as typeof validTabs[number])
    : "accounts";

  const [activeTab, setActiveTab] = useState<typeof validTabs[number]>(initialTab);

  const focusOrderId = activeTab === initialTab ? initialOrderParam : undefined;

  const handleTabChange = (next: typeof validTabs[number]) => {
    setActiveTab(next);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", next);
      url.searchParams.delete("order");
      window.history.replaceState({}, "", url.toString());
    }
  };

  const { data: accounts, isLoading } = useQuery<AdminAccount[]>({
    queryKey: ["/api/admin/accounts"],
  });

  const { data: stats } = useQuery<AdminStats>({
    queryKey: ["/api/admin/stats"],
  });

  const impersonateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/impersonate/${userId}`);
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Entering dashboard", description: `Viewing as ${data.fullName || data.email}` });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.clear();
      setTimeout(() => setLocation("/dashboard"), 300);
    },
    onError: () => toast({ title: "Failed to impersonate", variant: "destructive" }),
  });

  const handleImpersonate = (userId: string) => {
    impersonateMutation.mutate(userId);
  };

  const filtered = (accounts || []).filter(acc => {
    if (!search) return true;
    const s = search.toLowerCase();
    return acc.email.toLowerCase().includes(s) ||
      acc.fullName.toLowerCase().includes(s) ||
      acc.locations.some(l => l.name.toLowerCase().includes(s));
  });

  const tabs = [
    { id: "accounts" as const, label: "Accounts", icon: Users },
    { id: "api-usage" as const, label: "API Usage", icon: Activity },
    { id: "schema-installs" as const, label: "Schema Installs", icon: Braces },
    { id: "sweeper" as const, label: "Sweeper", icon: RefreshCw },
    { id: "bug-reports" as const, label: "Bug Reports", icon: Bug },
    { id: "ff-links" as const, label: "F&F Links", icon: Gift },
  ];

  return (
    <div className="min-h-screen bg-[#080c15] text-white">
      <div className="border-b border-white/[0.06] bg-[#0a0e17]">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoBlack} alt="Tork" className="h-32 w-auto block dark:hidden" />
            <img src={logoWhite} alt="Tork" className="h-32 w-auto hidden dark:block" />
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center">
                <Shield className="w-4 h-4 text-red-400" />
              </div>
              <span className="text-sm font-bold text-white font-['Outfit',sans-serif]">Admin Panel</span>
            </div>
          </div>
          <a href="/dashboard" className="text-[13px] text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1" data-testid="link-back-to-app">
            <LogOut className="w-3.5 h-3.5" /> Back to App
          </a>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard icon={Users} label="Total Accounts" value={stats.totalUsers} />
            <StatCard icon={MapPin} label="Total Locations" value={stats.totalLocations} sub={`${stats.connectedGbp} with GBP connected`} />
            <StatCard icon={Crown} label="Paid Accounts" value={stats.tiers.starter + stats.tiers.growth + stats.tiers.dominator} sub={`${stats.tiers.free} free`} />
            <StatCard icon={Building2} label="Tier Breakdown" value={`${stats.tiers.dominator}D / ${stats.tiers.growth}G / ${stats.tiers.starter}S`} sub={`${stats.tiers.free} Free`} />
            <StatCard icon={DollarSign} label="Client MRR" value={`$${stats.mrr.toLocaleString()}/mo`} sub={`${stats.paidClientCount} paid client${stats.paidClientCount !== 1 ? "s" : ""}`} />
          </div>
        )}

        <div className="flex items-center gap-2 mb-6 border-b border-white/[0.06]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`flex items-center gap-2 px-4 py-3 text-[13px] font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-500 text-indigo-400"
                  : "border-transparent text-white/40 hover:text-white/60"
              }`}
              onClick={() => handleTabChange(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "accounts" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                <input
                  type="text"
                  placeholder="Search accounts, emails, or locations…"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[13px] text-white/80 placeholder:text-white/25 focus:outline-none focus:border-indigo-500/40"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="input-admin-search"
                />
              </div>
              <div className="text-[12px] text-white/30">{filtered.length} account{filtered.length !== 1 ? "s" : ""}</div>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 rounded-xl bg-white/[0.03] animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map(acc => (
                  <AccountRow key={acc.id} account={acc} onImpersonate={handleImpersonate} currentUserId={currentUser?.id || ""} />
                ))}
                {filtered.length === 0 && (
                  <div className="text-center py-12 text-white/25 text-[14px]">No accounts found</div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "api-usage" && <ApiUsageTab />}

        {activeTab === "schema-installs" && <SchemaInstallsTab focusOrderId={focusOrderId} />}

        {activeTab === "sweeper" && <SweeperTab focusOrderId={focusOrderId} />}

        {activeTab === "bug-reports" && <BugReportsTab />}

        {activeTab === "ff-links" && <RedemptionCodesTab />}
      </div>
    </div>
  );
}

type SchemaOrderRow = {
  id: string;
  locationId: string;
  userId: string;
  packageType: "basic_schema" | "super_schema";
  status: string;
  amount: number;
  discountAmount: number;
  createdAt: string | null;
  paidAt: string | null;
  installedAt: string | null;
  installedBy: string | null;
  installNotes: string | null;
  jsonLdPayload: string | null;
  location: {
    id: string;
    name: string;
    businessName: string | null;
    website: string | null;
    city: string | null;
    state: string | null;
  } | null;
  user: {
    id: string;
    email: string | null;
    username: string | null;
    fullName: string | null;
  } | null;
};

const SUPER_SCHEMA_TEMPLATE = `{
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "WebSite", "@id": "https://example.com/#website", "url": "https://example.com/", "name": "Business Name" },
    { "@type": "WebPage", "@id": "https://example.com/#webpage", "url": "https://example.com/", "isPartOf": { "@id": "https://example.com/#website" } },
    { "@type": "Organization", "@id": "https://example.com/#org", "name": "Business Name", "url": "https://example.com/" },
    { "@type": "LocalBusiness", "@id": "https://example.com/#localbusiness", "name": "Business Name", "telephone": "+1-555-555-5555", "address": {"@type":"PostalAddress","streetAddress":"...","addressLocality":"...","addressRegion":"...","postalCode":"..."} },
    { "@type": "Service", "@id": "https://example.com/#service-1", "name": "Service Name", "provider": { "@id": "https://example.com/#localbusiness" } },
    { "@type": "FAQPage", "@id": "https://example.com/#faq", "mainEntity": [] },
    { "@type": "ImageObject", "@id": "https://example.com/#hero-image", "url": "https://example.com/hero.jpg" },
    { "@type": "Person", "@id": "https://example.com/#owner", "name": "Owner Name", "worksFor": { "@id": "https://example.com/#org" } }
  ]
}`;

const BASIC_SCHEMA_TEMPLATE = `{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Business Name",
  "telephone": "+1-555-555-5555",
  "url": "https://example.com/",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "...",
    "addressLocality": "...",
    "addressRegion": "...",
    "postalCode": "..."
  }
}`;

function SchemaInstallsTab({ focusOrderId }: { focusOrderId?: string }) {
  const { toast } = useToast();
  const [packageFilter, setPackageFilter] = useState<"all" | "basic_schema" | "super_schema">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "awaiting" | "installed" | "abandoned">(focusOrderId ? "all" : "awaiting");
  const [expanded, setExpanded] = useState<string | null>(focusOrderId || null);
  const [drafts, setDrafts] = useState<Record<string, { jsonLdPayload: string; installNotes: string }>>({});

  const queryKey = ["/api/admin/schema-orders", packageFilter];
  const { data: orders, isLoading } = useQuery<SchemaOrderRow[]>({
    queryKey,
    queryFn: async () => {
      const url = packageFilter === "all"
        ? "/api/admin/schema-orders"
        : `/api/admin/schema-orders?packageType=${packageFilter}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load schema orders");
      return res.json();
    },
  });

  const installMutation = useMutation({
    mutationFn: async ({ id, jsonLdPayload, installNotes }: { id: string; jsonLdPayload: string; installNotes: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/schema-orders/${id}/install`, {
        jsonLdPayload,
        installNotes: installNotes || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/schema-orders"] });
      toast({ title: "Marked installed", description: "Customer email sent." });
    },
    onError: (err: Error) => {
      toast({ title: "Install failed", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, jsonLdPayload, installNotes }: { id: string; jsonLdPayload?: string; installNotes?: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/schema-orders/${id}`, {
        ...(jsonLdPayload !== undefined ? { jsonLdPayload } : {}),
        ...(installNotes !== undefined ? { installNotes } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/schema-orders"] });
      toast({ title: "Saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const allOrders = orders || [];
  const isSupersededAbandoned = (o: SchemaOrderRow) => {
    if (o.status !== "abandoned") return false;
    const t = o.createdAt ? new Date(o.createdAt).getTime() : 0;
    return allOrders.some((other) =>
      other.id !== o.id &&
      other.locationId === o.locationId &&
      other.packageType === o.packageType &&
      (other.createdAt ? new Date(other.createdAt).getTime() : 0) > t,
    );
  };
  const filtered = allOrders.filter((o) => {
    if (isSupersededAbandoned(o)) return false;
    if (statusFilter === "awaiting") return o.status === "paid" && !o.installedAt;
    if (statusFilter === "installed") return !!o.installedAt;
    if (statusFilter === "abandoned") return o.status === "abandoned";
    return true;
  });

  const counts = {
    paid: allOrders.filter((o) => o.status === "paid").length,
    awaiting: allOrders.filter((o) => o.status === "paid" && !o.installedAt).length,
    installed: allOrders.filter((o) => !!o.installedAt).length,
    abandoned: allOrders.filter((o) => o.status === "abandoned" && !isSupersededAbandoned(o)).length,
  };

  const setDraft = (id: string, patch: Partial<{ jsonLdPayload: string; installNotes: string }>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        jsonLdPayload: prev[id]?.jsonLdPayload ?? "",
        installNotes: prev[id]?.installNotes ?? "",
        ...patch,
      },
    }));
  };

  const draftFor = (order: SchemaOrderRow) => {
    if (drafts[order.id]) return drafts[order.id];
    return {
      jsonLdPayload: order.jsonLdPayload || "",
      installNotes: order.installNotes || "",
    };
  };

  const fmtDate = (s: string | null | undefined) => {
    if (!s) return "—";
    return new Date(s).toLocaleString();
  };

  return (
    <div className="space-y-4" data-testid="tab-schema-installs">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] text-white/50 mr-2">
          {counts.paid} paid orders ·{" "}
          <span className="text-orange-400">{counts.awaiting} awaiting install</span> ·{" "}
          <span className="text-emerald-400">{counts.installed} installed</span>
          {counts.abandoned > 0 && (
            <>
              {" "}·{" "}
              <span className="text-rose-400">{counts.abandoned} abandoned</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          {(["all", "basic_schema", "super_schema"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPackageFilter(p)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${
                packageFilter === p
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white"
              }`}
              data-testid={`button-schema-pkg-${p}`}
            >
              {p === "all" ? "All packages" : p === "basic_schema" ? "Basic" : "Super"}
            </button>
          ))}
          <div className="w-px h-5 bg-white/10 mx-1" />
          {(["awaiting", "installed", "abandoned", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${
                statusFilter === s
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white"
              }`}
              data-testid={`button-schema-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-white/25 text-[14px]" data-testid="text-no-schema-orders">
          No schema orders match the current filter.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => {
            const isOpen = expanded === o.id;
            const isInstalled = !!o.installedAt;
            const isAbandoned = o.status === "abandoned";
            const isSuper = o.packageType === "super_schema";
            const draft = draftFor(o);
            const Icon = isSuper ? Network : Braces;
            const buyerLabel = o.user?.fullName || o.user?.email || o.user?.username || o.userId.slice(0, 8);
            const locationLabel = o.location?.name || o.location?.businessName || o.locationId.slice(0, 8);
            const cityState = [o.location?.city, o.location?.state].filter(Boolean).join(", ");

            return (
              <div
                key={o.id}
                className={`bg-white/[0.02] border rounded-lg overflow-hidden ${isAbandoned ? "border-rose-500/20" : "border-white/[0.06]"}`}
                data-testid={`row-schema-order-${o.id}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : o.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] text-left"
                  data-testid={`button-toggle-schema-order-${o.id}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAbandoned ? "bg-rose-500/15" : "bg-purple-500/15"}`}>
                    <Icon className={`w-4 h-4 ${isAbandoned ? "text-rose-400" : "text-purple-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-white truncate">
                      {locationLabel}
                      {cityState && <span className="text-white/40 font-normal"> · {cityState}</span>}
                    </div>
                    <div className="text-[12px] text-white/40 truncate">
                      {buyerLabel} ·{" "}
                      {isAbandoned
                        ? `started ${fmtDate(o.createdAt)}`
                        : `paid ${fmtDate(o.paidAt)}`}
                      {" "}· ${(o.amount / 100).toFixed(2)}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-purple-500/15 text-purple-300 border-purple-500/30">
                    {isSuper ? "Super" : "Basic"}
                  </span>
                  {isAbandoned ? (
                    <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-rose-500/15 text-rose-400 border-rose-500/30 flex items-center gap-1" data-testid={`badge-schema-order-status-${o.id}`}>
                      <AlertCircle className="w-3 h-3" /> Checkout expired
                    </span>
                  ) : isInstalled ? (
                    <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-emerald-500/15 text-emerald-400 border-emerald-500/30 flex items-center gap-1" data-testid={`badge-schema-order-status-${o.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Installed
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-orange-500/15 text-orange-400 border-orange-500/30" data-testid={`badge-schema-order-status-${o.id}`}>
                      Awaiting install
                    </span>
                  )}
                </button>

                {isOpen && isAbandoned && (
                  <div className="border-t border-rose-500/15 p-4 text-[12px] text-white/60 bg-rose-500/[0.04]">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
                      <div>
                        <div className="text-rose-300 font-semibold mb-1">Checkout abandoned</div>
                        <div>
                          The Whop checkout session expired before payment. The customer can start a fresh
                          checkout from their Citations page using the <span className="text-white">Start over</span> button.
                          No install action is required here.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isOpen && !isAbandoned && (
                  <div className="border-t border-white/[0.06] p-4 space-y-4 bg-black/20">
                    <div className="grid sm:grid-cols-2 gap-3 text-[12px] text-white/60">
                      <div>
                        <div className="text-white/40 mb-0.5">Customer</div>
                        <div className="text-white">{buyerLabel}</div>
                        {o.user?.email && <div className="text-white/40">{o.user.email}</div>}
                      </div>
                      <div>
                        <div className="text-white/40 mb-0.5">Location</div>
                        <div className="text-white">{locationLabel}</div>
                        {o.location?.website && (
                          <a
                            href={o.location.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1"
                            data-testid={`link-schema-order-website-${o.id}`}
                          >
                            {o.location.website}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <div>
                        <div className="text-white/40 mb-0.5">Paid</div>
                        <div className="text-white">{fmtDate(o.paidAt)}</div>
                      </div>
                      <div>
                        <div className="text-white/40 mb-0.5">Installed</div>
                        <div className="text-white">{fmtDate(o.installedAt)}</div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-[12px] font-semibold text-white/60 flex items-center gap-1.5">
                          <Code2 className="w-3.5 h-3.5" /> JSON-LD bundle
                        </label>
                        {!isInstalled && !draft.jsonLdPayload && (
                          <button
                            type="button"
                            onClick={() => setDraft(o.id, { jsonLdPayload: isSuper ? SUPER_SCHEMA_TEMPLATE : BASIC_SCHEMA_TEMPLATE })}
                            className="text-[11px] text-indigo-400 hover:text-indigo-300"
                            data-testid={`button-schema-template-${o.id}`}
                          >
                            Insert {isSuper ? "Super" : "Basic"} template
                          </button>
                        )}
                      </div>
                      <textarea
                        className="w-full h-72 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[12px] font-mono text-white/90 leading-relaxed focus:outline-none focus:border-indigo-500/40"
                        value={draft.jsonLdPayload}
                        onChange={(e) => setDraft(o.id, { jsonLdPayload: e.target.value })}
                        placeholder={`Paste the validated JSON-LD ${isSuper ? "@graph bundle" : "block"} that will be installed sitewide.`}
                        readOnly={isInstalled}
                        data-testid={`textarea-schema-payload-${o.id}`}
                      />
                    </div>

                    <div>
                      <label className="text-[12px] font-semibold text-white/60 mb-2 block">Internal install notes</label>
                      <textarea
                        className="w-full h-20 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white/90 focus:outline-none focus:border-indigo-500/40"
                        value={draft.installNotes}
                        onChange={(e) => setDraft(o.id, { installNotes: e.target.value })}
                        placeholder="Optional notes shown in the customer email (e.g. install location, validation results)."
                        data-testid={`textarea-schema-notes-${o.id}`}
                      />
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {!isInstalled ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!draft.jsonLdPayload || installMutation.isPending}
                            onClick={() => installMutation.mutate({
                              id: o.id,
                              jsonLdPayload: draft.jsonLdPayload,
                              installNotes: draft.installNotes,
                            })}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid={`button-mark-installed-${o.id}`}
                          >
                            {installMutation.isPending && installMutation.variables?.id === o.id ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Mark installed & email customer
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={updateMutation.isPending}
                            onClick={() => updateMutation.mutate({
                              id: o.id,
                              jsonLdPayload: draft.jsonLdPayload,
                              installNotes: draft.installNotes,
                            })}
                            className="text-white/60 hover:text-white"
                            data-testid={`button-save-draft-${o.id}`}
                          >
                            Save draft
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={updateMutation.isPending}
                          onClick={() => updateMutation.mutate({
                            id: o.id,
                            jsonLdPayload: draft.jsonLdPayload,
                            installNotes: draft.installNotes,
                          })}
                          className="text-white/60 hover:text-white"
                          data-testid={`button-update-install-${o.id}`}
                        >
                          Update bundle / notes
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type BugReportRow = {
  id: string;
  userId: string;
  locationId: string | null;
  title: string;
  description: string;
  severity: string;
  url: string | null;
  userAgent: string | null;
  consoleErrors: { ts: number; message: string }[] | null;
  posthogSessionId: string | null;
  posthogReplayUrl: string | null;
  posthogDistinctId: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
  user: { email: string | null; fullName: string | null } | null;
};

const SEVERITY_STYLES: Record<string, string> = {
  low: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
};

const STATUS_STYLES: Record<string, string> = {
  new: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  in_progress: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  wont_fix: "bg-white/[0.06] text-white/40 border-white/10",
};

interface RedemptionCodeRow {
  id: string;
  code: string;
  tier: string;
  label: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  active: boolean;
  createdAt: string;
}

function RedemptionCodesTab() {
  const { toast } = useToast();
  const [tier, setTier] = useState<"starter" | "growth" | "dominator">("dominator");
  const [label, setLabel] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [customCode, setCustomCode] = useState("");

  const { data: codes, isLoading } = useQuery<RedemptionCodeRow[]>({
    queryKey: ["/api/admin/redemption-codes"],
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: any = { tier, label: label.trim() || undefined };
      const max = parseInt(maxRedemptions, 10);
      if (!isNaN(max) && max > 0) body.maxRedemptions = max;
      if (customCode.trim()) body.code = customCode.trim();
      const res = await apiRequest("POST", "/api/admin/redemption-codes", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/redemption-codes"] });
      setLabel("");
      setMaxRedemptions("");
      setCustomCode("");
      toast({ title: "Invite link created" });
    },
    onError: (e: any) => toast({ title: e?.message || "Failed to create link", variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/redemption-codes/${id}`, { active });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/redemption-codes"] });
    },
    onError: () => toast({ title: "Update failed", variant: "destructive" }),
  });

  const copyLink = (code: string) => {
    const url = `${window.location.origin}/redeem/${code}`;
    navigator.clipboard?.writeText(url);
    toast({ title: "Link copied", description: url });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-amber-400" />
          <h3 className="text-[14px] font-semibold text-white">Create a Friends &amp; Family link</h3>
        </div>
        <p className="text-[12px] text-white/40 mb-4">
          Anyone who opens this link and signs up (or logs in) gets the selected tier comped — no Whop, no credit card.
          Access never expires until you deactivate the link or remove the comp from their account.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-white/40 mb-1">Tier</label>
            <select
              value={tier}
              onChange={e => setTier(e.target.value as any)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 focus:outline-none focus:border-indigo-500/40"
              data-testid="select-ff-tier"
            >
              <option value="dominator">Dominator</option>
              <option value="growth">Growth</option>
              <option value="starter">Starter</option>
            </select>
          </div>
          <div className="w-44">
            <label className="block text-[11px] text-white/40 mb-1">Custom code (optional)</label>
            <input
              type="text"
              value={customCode}
              onChange={e => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="auto-generated"
              maxLength={32}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 placeholder:text-white/25 font-mono tracking-wider focus:outline-none focus:border-indigo-500/40"
              data-testid="input-ff-code"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] text-white/40 mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Friends & Family"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 placeholder:text-white/25 focus:outline-none focus:border-indigo-500/40"
              data-testid="input-ff-label"
            />
          </div>
          <div className="w-32">
            <label className="block text-[11px] text-white/40 mb-1">Max uses (blank = ∞)</label>
            <input
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={e => setMaxRedemptions(e.target.value)}
              placeholder="∞"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 placeholder:text-white/25 focus:outline-none focus:border-indigo-500/40"
              data-testid="input-ff-max"
            />
          </div>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500/90 hover:bg-indigo-500 text-white text-[13px] font-semibold transition-colors disabled:opacity-50"
            data-testid="button-create-ff-link"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create link
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : (codes || []).length === 0 ? (
        <div className="text-center py-12 text-white/25 text-[14px]" data-testid="text-no-ff-links">No invite links yet</div>
      ) : (
        <div className="space-y-2">
          {(codes || []).map(c => {
            const exhausted = c.maxRedemptions != null && c.redemptionCount >= c.maxRedemptions;
            return (
              <div
                key={c.id}
                className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-4 flex items-center gap-4 flex-wrap"
                data-testid={`row-ff-link-${c.id}`}
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <code className="text-[15px] font-bold text-white tracking-wider" data-testid={`text-ff-code-${c.id}`}>{c.code}</code>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/15 text-amber-400 border border-amber-500/30">{c.tier}</span>
                    {!c.active && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/[0.06] text-white/40 border border-white/10">disabled</span>}
                    {exhausted && c.active && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/15 text-red-400 border border-red-500/30">limit reached</span>}
                  </div>
                  {c.label && <p className="text-[12px] text-white/40 mt-0.5">{c.label}</p>}
                  <p className="text-[11px] text-white/30 mt-0.5" data-testid={`text-ff-count-${c.id}`}>
                    {c.redemptionCount} redeemed{c.maxRedemptions != null ? ` / ${c.maxRedemptions} max` : " · unlimited"}
                  </p>
                </div>
                <button
                  onClick={() => copyLink(c.code)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-[12px] font-medium transition-colors"
                  data-testid={`button-copy-ff-${c.id}`}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
                <button
                  onClick={() => toggleMutation.mutate({ id: c.id, active: !c.active })}
                  disabled={toggleMutation.isPending}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors disabled:opacity-50 ${
                    c.active
                      ? "bg-red-500/[0.08] border-red-500/20 text-red-400 hover:bg-red-500/15"
                      : "bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/15"
                  }`}
                  data-testid={`button-toggle-ff-${c.id}`}
                >
                  <Power className="w-3.5 h-3.5" /> {c.active ? "Deactivate" : "Reactivate"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BugReportsTab() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: reports, isLoading } = useQuery<BugReportRow[]>({
    queryKey: ["/api/admin/bug-reports", statusFilter],
    queryFn: async () => {
      const url = statusFilter === "all"
        ? "/api/admin/bug-reports"
        : `/api/admin/bug-reports?status=${statusFilter}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { status?: string; adminNotes?: string } }) => {
      const res = await apiRequest("PATCH", `/api/admin/bug-reports/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/bug-reports"] });
      toast({ title: "Updated" });
    },
    onError: () => toast({ title: "Update failed", variant: "destructive" }),
  });

  const counts = {
    total: reports?.length || 0,
    new: reports?.filter(r => r.status === "new").length || 0,
    in_progress: reports?.filter(r => r.status === "in_progress").length || 0,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] text-white/50 mr-2">
          {counts.total} reports · <span className="text-orange-400">{counts.new} new</span> · <span className="text-blue-400">{counts.in_progress} in progress</span>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          {["all", "new", "in_progress", "resolved", "wont_fix"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${
                statusFilter === s
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white"
              }`}
              data-testid={`button-bug-filter-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : (reports || []).length === 0 ? (
        <div className="text-center py-12 text-white/25 text-[14px]" data-testid="text-no-bug-reports">No bug reports</div>
      ) : (
        <div className="space-y-2">
          {(reports || []).map(r => {
            const isOpen = expanded === r.id;
            return (
              <div
                key={r.id}
                className="bg-white/[0.02] border border-white/[0.06] rounded-lg overflow-hidden"
                data-testid={`row-bug-report-${r.id}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] text-left"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-white truncate">{r.title}</div>
                    <div className="text-[12px] text-white/40 truncate">
                      {r.user?.fullName || r.user?.email || r.userId.slice(0, 8)} · {new Date(r.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium border ${SEVERITY_STYLES[r.severity] || ""}`}>
                    {r.severity}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium border ${STATUS_STYLES[r.status] || ""}`}>
                    {r.status.replace("_", " ")}
                  </span>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-2 border-t border-white/[0.06] space-y-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Description</div>
                      <div className="text-[13px] text-white/80 whitespace-pre-wrap">{r.description}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      <div>
                        <div className="text-white/40">URL</div>
                        <div className="text-white/70 truncate" title={r.url || ""}>{r.url || "—"}</div>
                      </div>
                      <div>
                        <div className="text-white/40">Browser</div>
                        <div className="text-white/70 truncate" title={r.userAgent || ""}>{r.userAgent?.split(") ")[0]?.split("(")[1] || r.userAgent || "—"}</div>
                      </div>
                      <div>
                        <div className="text-white/40">Location ID</div>
                        <div className="text-white/70 font-mono">{r.locationId || "—"}</div>
                      </div>
                      <div>
                        <div className="text-white/40">PostHog Session</div>
                        <div className="text-white/70 font-mono truncate">{r.posthogSessionId || "—"}</div>
                      </div>
                    </div>

                    {r.posthogReplayUrl && (
                      <a
                        href={r.posthogReplayUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[12px] font-medium hover:bg-orange-500/20"
                        data-testid={`link-replay-${r.id}`}
                      >
                        <ExternalLinkIcon className="w-3.5 h-3.5" />
                        Watch PostHog session replay
                      </a>
                    )}

                    {r.consoleErrors && r.consoleErrors.length > 0 && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-white/40 mb-1">
                          Console Errors ({r.consoleErrors.length})
                        </div>
                        <div className="bg-black/40 border border-white/[0.06] rounded p-2 max-h-48 overflow-auto">
                          {r.consoleErrors.slice().reverse().map((e, i) => (
                            <div key={i} className="text-[11px] text-red-300/90 font-mono whitespace-pre-wrap mb-1.5">
                              <span className="text-white/30">[{new Date(e.ts).toLocaleTimeString()}]</span> {e.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-[12px] text-white/40 mr-1">Status:</span>
                      {["new", "in_progress", "resolved", "wont_fix"].map(s => (
                        <button
                          key={s}
                          onClick={() => updateMutation.mutate({ id: r.id, data: { status: s } })}
                          disabled={r.status === s || updateMutation.isPending}
                          className={`px-2.5 py-1 rounded text-[11px] font-medium border ${
                            r.status === s
                              ? STATUS_STYLES[s]
                              : "bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.05]"
                          }`}
                          data-testid={`button-set-status-${s}-${r.id}`}
                        >
                          {s.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type SweeperEvent = {
  id: string;
  kind: "reconciled" | "abandoned";
  at: string | null;
  order: {
    id: string;
    locationId: string;
    userId: string;
    packageType: string;
    status: string;
    amount: number;
    paidAt: string | null;
    abandonedAt: string | null;
    paidVia: string | null;
    stripeSessionId: string | null;
  };
  location: {
    id: string;
    name: string;
    businessName: string | null;
    city: string | null;
    state: string | null;
  } | null;
  user: {
    id: string;
    email: string | null;
    username: string | null;
    fullName: string | null;
  } | null;
};

type SweeperActivity = {
  counts: {
    reconciled: { h24: number; d7: number; d30: number };
    abandoned: { h24: number; d7: number; d30: number };
  };
  events: SweeperEvent[];
  series: Array<{ date: string; reconciled: number; abandoned: number }>;
};

function SweeperTab({ focusOrderId }: { focusOrderId?: string }) {
  const [filter, setFilter] = useState<"all" | "reconciled" | "abandoned">("all");
  const { toast } = useToast();

  const { data, isLoading } = useQuery<SweeperActivity>({
    queryKey: ["/api/admin/sweeper-activity"],
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!focusOrderId || !data) return;
    const match = data.events.find((e) => e.order.id === focusOrderId);
    if (match) setExpanded(match.id);
  }, [focusOrderId, data]);

  const copyPermalink = (orderId: string) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "sweeper");
    url.searchParams.set("order", orderId);
    navigator.clipboard.writeText(url.toString()).then(
      () => toast({ title: "Permalink copied", description: "Share this link to drill into this exact order." }),
      () => toast({ title: "Copy failed", description: url.toString(), variant: "destructive" }),
    );
  };

  const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString() : "—");
  const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const filtered = (data?.events || []).filter((e) => filter === "all" || e.kind === filter);

  const maxBar = Math.max(
    1,
    ...((data?.series || []).map((s) => Math.max(s.reconciled, s.abandoned))),
  );

  return (
    <div className="space-y-6" data-testid="tab-sweeper">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SweeperStatCard
          label="Self-healed (24h)"
          value={data?.counts.reconciled.h24 ?? 0}
          sub={`${data?.counts.reconciled.d7 ?? 0} this week · ${data?.counts.reconciled.d30 ?? 0} this month`}
          tone="emerald"
          icon={CheckCircle2}
          testId="stat-sweeper-reconciled"
        />
        <SweeperStatCard
          label="Abandoned (24h)"
          value={data?.counts.abandoned.h24 ?? 0}
          sub={`${data?.counts.abandoned.d7 ?? 0} this week · ${data?.counts.abandoned.d30 ?? 0} this month`}
          tone="orange"
          icon={AlertCircle}
          testId="stat-sweeper-abandoned"
        />
        <SweeperStatCard
          label="Total events (30d)"
          value={(data?.counts.reconciled.d30 ?? 0) + (data?.counts.abandoned.d30 ?? 0)}
          sub="Recovered + abandoned by sweeper"
          tone="indigo"
          icon={RefreshCw}
          testId="stat-sweeper-total"
        />
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-semibold text-white/80">Last 30 days</div>
          <div className="flex items-center gap-3 text-[11px] text-white/50">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-400/80" /> Reconciled
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-orange-400/80" /> Abandoned
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="h-32 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-white/30" />
          </div>
        ) : (
          <div className="flex items-end gap-1 h-32" data-testid="chart-sweeper-30d">
            {(data?.series || []).map((d) => {
              const rh = Math.round((d.reconciled / maxBar) * 100);
              const ah = Math.round((d.abandoned / maxBar) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={`${d.date}: ${d.reconciled} reconciled, ${d.abandoned} abandoned`}>
                  <div className="w-full flex items-end gap-0.5 h-full">
                    <div
                      className="flex-1 bg-emerald-500/70 rounded-sm min-h-[1px]"
                      style={{ height: `${rh}%` }}
                    />
                    <div
                      className="flex-1 bg-orange-500/70 rounded-sm min-h-[1px]"
                      style={{ height: `${ah}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13px] text-white/50 mr-2">
            {filtered.length} event{filtered.length !== 1 ? "s" : ""} (last 30 days, max 100)
          </div>
          <div className="flex items-center gap-1 ml-auto">
            {(["all", "reconciled", "abandoned"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${
                  filter === f
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white"
                }`}
                data-testid={`button-sweeper-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-white/25 text-[14px]" data-testid="text-no-sweeper-events">
            No sweeper activity in this window.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((e) => {
              const isOpen = expanded === e.id;
              const locLabel = e.location?.businessName || e.location?.name || e.order.locationId.slice(0, 8);
              const cityState = [e.location?.city, e.location?.state].filter(Boolean).join(", ");
              const userLabel = e.user?.fullName || e.user?.email || e.user?.username || e.order.userId.slice(0, 8);
              const isSchema = e.order.packageType === "basic_schema" || e.order.packageType === "super_schema";
              const stripeSessionUrl = e.order.stripeSessionId
                ? `https://dashboard.stripe.com/checkout/sessions/${e.order.stripeSessionId}`
                : null;
              return (
                <div
                  key={e.id}
                  className="bg-white/[0.02] border border-white/[0.06] rounded-lg overflow-hidden"
                  data-testid={`row-sweeper-event-${e.id}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] text-left"
                    data-testid={`button-toggle-sweeper-event-${e.id}`}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                    {e.kind === "reconciled" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border bg-emerald-500/15 text-emerald-400 border-emerald-500/30 shrink-0">
                        <CheckCircle2 className="w-3 h-3" /> Self-healed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border bg-orange-500/15 text-orange-400 border-orange-500/30 shrink-0">
                        <AlertCircle className="w-3 h-3" /> Abandoned
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-white truncate">
                        {locLabel}
                        {cityState && <span className="text-white/40 font-normal"> · {cityState}</span>}
                      </div>
                      <div className="text-[12px] text-white/40 truncate">
                        Order <span className="font-mono text-white/60" data-testid={`text-sweeper-order-id-short-${e.order.id}`}>{e.order.id.slice(0, 8)}</span>
                        {" · "}{userLabel}{" · "}{fmtDate(e.at)}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-white/[0.04] text-white/60 border-white/10 shrink-0">
                      {e.order.packageType}
                    </span>
                    <span className="text-[13px] text-white/70 whitespace-nowrap">{fmtMoney(e.order.amount)}</span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-white/[0.06] p-4 space-y-4 bg-black/20" data-testid={`detail-sweeper-event-${e.id}`}>
                      <div className="grid sm:grid-cols-2 gap-3 text-[12px] text-white/60">
                        <div>
                          <div className="text-white/40 mb-0.5">Order ID</div>
                          <div className="text-white font-mono text-[12px] break-all" data-testid={`text-sweeper-order-id-${e.order.id}`}>{e.order.id}</div>
                        </div>
                        <div>
                          <div className="text-white/40 mb-0.5">Status</div>
                          <div className="text-white capitalize">{e.order.status}{e.order.paidVia ? ` · via ${e.order.paidVia}` : ""}</div>
                        </div>
                        <div>
                          <div className="text-white/40 mb-0.5">Customer</div>
                          <div className="text-white">{userLabel}</div>
                          {e.user?.email && <div className="text-white/40">{e.user.email}</div>}
                        </div>
                        <div>
                          <div className="text-white/40 mb-0.5">Location</div>
                          <div className="text-white">{locLabel}</div>
                          {cityState && <div className="text-white/40">{cityState}</div>}
                        </div>
                        <div>
                          <div className="text-white/40 mb-0.5">Paid at</div>
                          <div className="text-white">{fmtDate(e.order.paidAt)}</div>
                        </div>
                        <div>
                          <div className="text-white/40 mb-0.5">Abandoned at</div>
                          <div className="text-white">{fmtDate(e.order.abandonedAt)}</div>
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-white/40 mb-0.5">Stripe checkout session</div>
                          <div className="text-white font-mono text-[11px] break-all">{e.order.stripeSessionId || "—"}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap pt-1">
                        <button
                          type="button"
                          onClick={() => copyPermalink(e.order.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium border bg-indigo-500/15 text-indigo-300 border-indigo-500/30 hover:bg-indigo-500/25"
                          data-testid={`button-sweeper-permalink-${e.order.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Copy investigation link
                        </button>
                        {isSchema && (
                          <a
                            href={`/admin?tab=schema-installs&order=${e.order.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium border bg-purple-500/15 text-purple-300 border-purple-500/30 hover:bg-purple-500/25"
                            data-testid={`link-sweeper-open-schema-${e.order.id}`}
                          >
                            <Braces className="w-3.5 h-3.5" /> Open in Schema Installs
                          </a>
                        )}
                        {stripeSessionUrl && (
                          <a
                            href={stripeSessionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium border bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
                            data-testid={`link-sweeper-stripe-${e.order.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> View Stripe session
                          </a>
                        )}
                        {e.location?.id && (
                          <a
                            href={`/dashboard?location=${e.location.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium border bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
                            data-testid={`link-sweeper-location-${e.order.id}`}
                          >
                            <MapPin className="w-3.5 h-3.5" /> Open location dashboard
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SweeperStatCard({
  label,
  value,
  sub,
  tone,
  icon: Icon,
  testId,
}: {
  label: string;
  value: number;
  sub: string;
  tone: "emerald" | "orange" | "indigo";
  icon: LucideIcon;
  testId: string;
}) {
  const toneClasses: Record<"emerald" | "orange" | "indigo", { bg: string; text: string }> = {
    emerald: { bg: "bg-emerald-500/15", text: "text-emerald-400" },
    orange: { bg: "bg-orange-500/15", text: "text-orange-400" },
    indigo: { bg: "bg-indigo-500/15", text: "text-indigo-400" },
  };
  const t = toneClasses[tone];
  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg ${t.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${t.text}`} />
        </div>
        <div className="text-[12px] text-white/50 font-medium">{label}</div>
      </div>
      <div className="text-[24px] font-bold text-white leading-none">{value}</div>
      <div className="text-[11px] text-white/40 mt-1.5">{sub}</div>
    </div>
  );
}
